import {
  ChangeTeamRootFnReq,
  ChangeTeamRootFnRes,
  ChangeTeamRootOperationData,
  Collections,
  Logger,
  OperationLogEntity,
  StorageItem,
} from '@uploadcam/common';
import { Functions, httpsCallable } from 'firebase/functions';
import { GetAuthDtoInteractor } from '../../../auth/domain/interactors/get-auth-dto.interactor';
import { CreateOperationLogInteractor } from '../../../operation-logs/domain/interactors/create-operation-log.interactor';
import { doc, Firestore, onSnapshot } from 'firebase/firestore';
import { Observable, Subject } from 'rxjs';

export interface ChangeTeamRootResult {
  updates: Observable<ChangeTeamRootOperationData>;
  response: Promise<ChangeTeamRootFnRes>;
}

export class ChangeTeamRootInteractor {
  /**
   * Must cover the server-side timeout of the `changeTeamRoot` function (15
   * minutes, see `functions/src/index.ts`) plus a margin so the server's own
   * response always arrives before the client gives up. The Firebase SDK
   * default is 70s, which would reject the client promise (and tear down the
   * operation progress listener) while the server keeps processing.
   */
  private static readonly TIMEOUT_MS = 16 * 60 * 1000;

  private readonly changeTeamRootFn = httpsCallable<ChangeTeamRootFnReq, ChangeTeamRootFnRes>(
    this.functions,
    'changeTeamRoot',
    { timeout: ChangeTeamRootInteractor.TIMEOUT_MS },
  );

  constructor(
    private readonly createOperationLog: CreateOperationLogInteractor,
    private readonly firestore: Firestore,
    private readonly functions: Functions,
    private readonly getAuthDto: GetAuthDtoInteractor,
    private readonly logger: Logger,
  ) {}

  public async execute(
    teamRoot: StorageItem,
    archiveCurrentAlbums: boolean,
    recreateUserDefaultAlbums: boolean,
  ): Promise<ChangeTeamRootResult> {
    this.logger.info('Initializing Team root change operation');

    const subject = new Subject<ChangeTeamRootOperationData>();
    const operation = await this.createOperationLog.execute({
      type: 'change-team-root',
      state: 'processing',
      progress: 0,
    });

    this.logger.info(`Operation ID: ${operation.id}`);

    const unsub = onSnapshot(
      doc(this.firestore, Collections.OperationLogs, operation.id),
      (doc) => {
        const data = doc.data();

        if (!data) {
          this.logger.warn('Operation document not found');
          return;
        }

        const opData = (data as OperationLogEntity).operation;

        if (opData.type === 'change-team-root') {
          const state = opData.state;

          this.logger.info('Operation updated:', opData);
          subject.next(opData);

          if (state === 'done' || state === 'error') {
            this.logger.info('Operation completed with state:', state);
            subject.complete();
            unsub();
          }
        }
      },
      (err) => {
        this.logger.error('Snapshot listener error', err);
        if (!subject.closed) {
          subject.error(err);
        }
        unsub();
      },
    );

    const response = this.changeTeamRootFn({
      auth: await this.getAuthDto.execute(),
      folder: teamRoot.toFolderPick(),
      operationDocId: operation.id,
      archiveCurrentAlbums,
      recreateUserDefaultAlbums,
    }).then((res) => res.data);

    response
      .then(() => {
        this.logger.info('Team root change operation completed');
      })
      .catch((err) => {
        this.logger.error('Team root change operation failed', err);
        if (!subject.closed) {
          subject.error(err);
        }
        unsub();
      });

    return {
      updates: subject.asObservable(),
      response,
    };
  }
}
